/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * DSA (Distributed System Agents) API Client
 * 
 * This module provides helper functions for calling the DSA APIs
 * for managing distributed agent coordination across Velocity instances.
 * 
 * API Structure:
 * - /api/dsa/status - Get DSA worker status
 * - /api/dsa/enable - Enable DSA for this instance
 * - /api/dsa/disable - Disable DSA worker
 * - /api/dsa/runs - Get DSA run history
 * - /api/dsa/runs/:agentName/trigger - Manually trigger a run
 * - /api/dsa/agents - List DSA agents from Velocity repo
 * - /api/dsa/schedule - Get schedule configuration
 * - /api/dsa/sync - Force sync with Velocity repo
 */

import type {
  DSAStatusResponse,
  DSARunsResponse,
  DSAWorkerStatus,
  DSAWorkerConfig,
  DSAAgent,
  DSARun,
  DSAScheduleConfig,
  DSASystemConfig,
  AgentEligibility,
  VelocityRepoConfig,
} from '@shared/schema/dsa';

// Base URL for main SkillShop server (where DSA API lives)
const getBaseUrl = () => {
  return 'http://localhost:5173';
};

// ============================================================================
// Status Endpoints
// ============================================================================

export interface DSAStatusResult {
  success: boolean;
  status?: DSAStatusResponse;
  error?: string;
}

/**
 * Get full DSA status including worker, repo, agents, and schedule
 */
export async function getDSAStatus(): Promise<DSAStatusResult> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/status`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch DSA status' };
    }
    
    return { success: true, status: data.data };
  } catch (error: any) {
    console.error('Error fetching DSA status:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get simplified worker status
 */
export async function getDSAWorkerStatus(): Promise<{ success: boolean; worker?: DSAWorkerStatus; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/worker`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch worker status' };
    }
    
    return { success: true, worker: data.data };
  } catch (error: any) {
    console.error('Error fetching worker status:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Velocity Repo Management Endpoints
// ============================================================================

export interface VelocityRepoStatus {
  exists: boolean;
  url?: string;
  provider?: 'github' | 'gitlab';
  lastSync?: string;
  error?: string;
}

/**
 * Check if a Velocity repo exists for the organization
 */
export async function checkVelocityRepo(): Promise<{ success: boolean; status?: VelocityRepoStatus; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/velocity-repo/status`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to check Velocity repo status' };
    }
    
    return { success: true, status: data.data };
  } catch (error: any) {
    console.error('Error checking Velocity repo:', error);
    return { success: false, error: error.message };
  }
}

export interface CreateVelocityRepoOptions {
  /** Organization name for the repo (used in repo name) */
  orgName: string;
  /** Git provider - required for remote repo creation */
  provider: 'github' | 'gitlab';
  /** Access token for the provider */
  accessToken: string;
  /** GitLab instance URL (only for GitLab) */
  instanceUrl?: string;
  /** If true, detect existing repo first before creating */
  detectExisting?: boolean;
  /** Make the repo private (default: true) */
  isPrivate?: boolean;
  /** Organization ID to persist velocityRepoUrl to org settings */
  organizationId?: string;
}

export interface CreateVelocityRepoResult {
  success: boolean;
  url?: string;
  created?: boolean;
  detected?: boolean;
  error?: string;
}

/**
 * Create a Velocity repo for the organization on the remote provider
 * This creates a remote repo on GitHub/GitLab, initializes it locally with DSA content,
 * and pushes to the remote.
 */
export async function createVelocityRepo(options: CreateVelocityRepoOptions): Promise<CreateVelocityRepoResult> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/velocity-repo/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || data.error || 'Failed to create Velocity repo' };
    }
    
    return { 
      success: true, 
      url: data.data?.url,
      created: data.data?.created,
      detected: data.data?.detected,
    };
  } catch (error: any) {
    console.error('Error creating Velocity repo:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Enable/Disable Endpoints
// ============================================================================

export interface EnableDSAOptions {
  velocityRepoUrl?: string;
  createRepo?: boolean;
  provider?: 'github' | 'gitlab';
  orgName?: string;
  /** If true, automatically detect an existing Velocity repo or create one for the organization */
  autoDetectOrCreate?: boolean;
}

/**
 * Enable DSA for this Velocity instance
 */
export async function enableDSA(options: EnableDSAOptions): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/enable`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to enable DSA' };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error enabling DSA:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Disable DSA worker
 */
export async function disableDSA(): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/disable`, {
      method: 'POST',
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to disable DSA' };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error disabling DSA:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Runs Endpoints
// ============================================================================

export interface DSARunsResult {
  success: boolean;
  runs?: DSARunsResponse;
  error?: string;
}

/**
 * Get DSA run history
 */
export async function getDSARuns(): Promise<DSARunsResult> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/runs`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch DSA runs' };
    }
    
    return { success: true, runs: data.data };
  } catch (error: any) {
    console.error('Error fetching DSA runs:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Trigger a DSA run for an agent
 */
export async function triggerDSARun(
  agentName: string,
  options?: { products?: string[]; priority?: string }
): Promise<{ success: boolean; runId?: string; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/runs/${agentName}/trigger`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options || {}),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to trigger DSA run' };
    }
    
    return { success: true, runId: data.data?.runId };
  } catch (error: any) {
    console.error('Error triggering DSA run:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Agents Endpoints
// ============================================================================

export interface DSAAgentsResult {
  success: boolean;
  agents?: DSAAgent[];
  error?: string;
}

/**
 * List DSA agents from Velocity repo
 */
export async function getDSAAgents(): Promise<DSAAgentsResult> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/agents`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch DSA agents' };
    }
    
    return { success: true, agents: data.data || [] };
  } catch (error: any) {
    console.error('Error fetching DSA agents:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get a specific DSA agent
 */
export async function getDSAAgent(agentName: string): Promise<{ success: boolean; agent?: DSAAgent; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/agents/${agentName}`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch DSA agent' };
    }
    
    return { success: true, agent: data.data };
  } catch (error: any) {
    console.error('Error fetching DSA agent:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get agent eligibility scores
 */
export async function getDSAAgentEligibility(): Promise<{ success: boolean; eligibility?: AgentEligibility[]; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/agents/eligibility`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch eligibility' };
    }
    
    return { success: true, eligibility: data.data || [] };
  } catch (error: any) {
    console.error('Error fetching eligibility:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Schedule Endpoints
// ============================================================================

/**
 * Get DSA schedule configuration
 */
export async function getDSASchedule(): Promise<{ success: boolean; schedule?: DSAScheduleConfig | null; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/schedule`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch schedule' };
    }
    
    return { success: true, schedule: data.data };
  } catch (error: any) {
    console.error('Error fetching schedule:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Sync Endpoints
// ============================================================================

/**
 * Force sync with Velocity repo
 */
export async function syncDSARepo(): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/sync`, {
      method: 'POST',
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to sync repo' };
    }
    
    return { success: true };
  } catch (error: any) {
    console.error('Error syncing repo:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Force poll cycle
 */
export async function triggerDSAPoll(): Promise<{ success: boolean; result?: any; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/poll`, {
      method: 'POST',
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to poll' };
    }
    
    return { success: true, result: data.data };
  } catch (error: any) {
    console.error('Error polling:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Configuration Endpoints
// ============================================================================

/**
 * Get DSA system configuration
 */
export async function getDSAConfig(): Promise<{ success: boolean; config?: DSASystemConfig | null; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/config`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch config' };
    }
    
    return { success: true, config: data.data };
  } catch (error: any) {
    console.error('Error fetching config:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get worker configuration
 */
export async function getDSAWorkerConfig(): Promise<{ success: boolean; config?: DSAWorkerConfig; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/config/worker`);
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to fetch worker config' };
    }
    
    return { success: true, config: data.data };
  } catch (error: any) {
    console.error('Error fetching worker config:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Update worker configuration
 */
export async function updateDSAWorkerConfig(
  updates: Partial<DSAWorkerConfig>
): Promise<{ success: boolean; config?: DSAWorkerConfig; error?: string }> {
  try {
    const response = await fetch(`${getBaseUrl()}/api/dsa/config/worker`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    
    if (!response.ok) {
      return { success: false, error: data.error?.message || 'Failed to update config' };
    }
    
    return { success: true, config: data.data };
  } catch (error: any) {
    console.error('Error updating config:', error);
    return { success: false, error: error.message };
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get status color for DSA run
 */
export function getDSARunStatusColor(status: string): string {
  switch (status) {
    case 'running': return 'bg-blue-100 text-blue-800';
    case 'completed': return 'bg-green-100 text-green-800';
    case 'failed': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-600';
  }
}

/**
 * Get status color for result
 */
export function getDSAResultColor(result: string | undefined): string {
  switch (result) {
    case 'pass': return 'text-green-600';
    case 'warning': return 'text-yellow-600';
    case 'fail': return 'text-red-600';
    default: return 'text-gray-600';
  }
}

/**
 * Format relative time
 */
export function formatRelativeTime(date: string | null): string {
  if (!date) return 'Never';
  
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}

/**
 * Get priority badge color
 */
export function getPriorityColor(priority: string): string {
  switch (priority) {
    case 'critical': return 'bg-red-100 text-red-800';
    case 'high': return 'bg-orange-100 text-orange-800';
    case 'medium': return 'bg-yellow-100 text-yellow-800';
    case 'low': return 'bg-gray-100 text-gray-600';
    default: return 'bg-gray-100 text-gray-600';
  }
}
